import { z } from 'zod'

/** Zeiterfassung (ADR 0023): meetings of a project are booked in voluro (os.voluro.de) after they are processed. */

export const TimeTrackingAccount = z.object({
  provider: z.literal('voluro'),
  keyPrefix: z.string(),
  status: z.enum(['active', 'error']),
  error: z.string().nullable(),
  updatedAt: z.string(),
})
export type TimeTrackingAccount = z.infer<typeof TimeTrackingAccount>

export const TimeTrackingStatus = z.object({ account: TimeTrackingAccount.nullable() })
export type TimeTrackingStatus = z.infer<typeof TimeTrackingStatus>

export const SaveTimeTrackingKey = z.object({
  apiKey: z
    .string()
    .trim()
    .regex(/^vk_\S{8,}$/, 'Der Schlüssel beginnt mit vk_.'),
})
export type SaveTimeTrackingKey = z.infer<typeof SaveTimeTrackingKey>

export const ExternalTask = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string().nullable(),
  parentId: z.string().nullable(),
})
export type ExternalTask = z.infer<typeof ExternalTask>

export const ExternalProject = z.object({
  id: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  status: z.string().nullable(),
  accountName: z.string().nullable(),
  tasks: z.array(ExternalTask),
})
export type ExternalProject = z.infer<typeof ExternalProject>

const Ref = z.object({ id: z.string().min(1).max(200), name: z.string().max(500) })

export const ContainerTimeTracking = z.object({
  enabled: z.boolean(),
  project: Ref.extend({ code: z.string().nullable() }),
  task: Ref.nullable(),
  enabledAt: z.string(),
})
export type ContainerTimeTracking = z.infer<typeof ContainerTimeTracking>

export const UpdateContainerTimeTracking = z.object({
  enabled: z.boolean(),
  project: Ref.extend({ code: z.string().max(100).nullable().optional() }),
  task: Ref.nullable().optional(),
})
export type UpdateContainerTimeTracking = z.infer<typeof UpdateContainerTimeTracking>

export const MeetingTimeEntryStatus = z.enum(['pending', 'booked', 'failed', 'skipped'])
export type MeetingTimeEntryStatus = z.infer<typeof MeetingTimeEntryStatus>

export const MeetingTimeEntry = z.object({
  status: MeetingTimeEntryStatus,
  externalEntryId: z.string().nullable(),
  projectName: z.string().nullable(),
  date: z.string().nullable(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  minutes: z.number().nullable(),
  description: z.string().nullable(),
  error: z.string().nullable(),
  updatedAt: z.string(),
})
export type MeetingTimeEntry = z.infer<typeof MeetingTimeEntry>

/** GET /meetings/:id/time-entry – `trackable`: the meeting's project has time tracking switched on. */
export const MeetingTimeEntryView = z.object({ trackable: z.boolean(), entry: MeetingTimeEntry.nullable() })
export type MeetingTimeEntryView = z.infer<typeof MeetingTimeEntryView>
